import TaskButton from "@/components/base/button/TaskButton";
import { FormEvent, Fragment, useEffect, useRef, useState } from "react";
import IconClose from "@/assets/icons/close.svg?react";
import IconDon from "@/assets/icons/don.svg?react";
import ExternalLink from "@/assets/icons/external-link.svg?react";
import EyeIcon from "@/assets/icons/eye.svg?react";
import { useTranslation } from "react-i18next";
import BaseButton from "@/components/base/button/BaseButton";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useMutation } from "@tanstack/react-query";
import { useFetch } from "@/lib/axios.config";
import {
  TaskAwardResponse,
  TaskList,
  TaskStatus,
  TaskStatusResponse,
} from "../tasks.types";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { formatNumber } from "@/shared/utils";
import { Label } from "@/components/ui/label";
import { openLink } from "@telegram-apps/sdk-react";
import { pasteClipboardContent } from "@/shared/utils/common";

async function claimAward(
  taskId: string,
  input?: string
): Promise<TaskStatusResponse> {
  const response = await useFetch.post<{ state: TaskStatusResponse }>(
    `/tasks/ClaimTaskAward/${taskId}`,
    {
      keyword: input ?? "",
    }
  );
  return response.data.state;
}

interface PromocodeDrawerProps {
  task: TaskList;
  getAward: () => Promise<TaskAwardResponse>;
}

const PromocodeDrawer = ({
  task,
  getAward: getAwardFn,
}: PromocodeDrawerProps) => {
  const { t } = useTranslation();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const closeDrawer = () => {
    setOpenDrawer(false);
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
    setInputValue("");
  };

  useEffect(() => {
    if (openDrawer) {
      setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 500);
    }
  }, [openDrawer]);

  const { mutateAsync: claimReward, isPending: isClaiming } = useMutation({
    mutationFn: () => {
      return claimAward(task.id ?? "", inputValue);
    },
  });

  const handleButtonClicked = async (e: FormEvent) => {
    e.preventDefault();

    if (!inputValue.length) return;
    const res = await claimReward();
    if (res === "COMPLETED" || res === "READY_AWARD") {
      const res = await getAwardFn();
      if (res === "COMPLETED") {
        closeDrawer();
      }
    } else {
      toast.error(t("incomplete_the_promo_task"));
    }
  };


  const handlePaste = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    const pastedContent = await pasteClipboardContent();
    setInputValue(pastedContent);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const isPending = isClaiming;

  return (
    <Fragment>
      {task.user_process?.status &&
      task.user_process.status === TaskStatus.Completed ? (
        <TaskButton status="COMPLETED" />
      ) : (
        <TaskButton status="INCOMPLETE" onClick={handleOpenDrawer} />
      )}
      <Drawer
        open={openDrawer}
        onOpenChange={(o) => {
          if (!o) closeDrawer();
        }}
      >
        <DrawerContent className="bg-gray-4" showTopThumb>
          <DrawerHeader className="flex justify-between items-center">
            <DrawerTitle className="text-lg">{t("promo_code")}</DrawerTitle>
            <DrawerClose>
              <IconClose className="size-6 text-gray-1 cursor-pointer" />
            </DrawerClose>
          </DrawerHeader>
          <form
            className="px-4 pb-safe-bottom"
            onSubmit={handleButtonClicked}
          >
            <div className="p-3 border border-white/08 rounded-10 bg-white/08">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="size-9 rounded-full bg-white/08 flex-center">
                    <EyeIcon className="size-6 text-white" strokeWidth={2} />
                  </span>
                  <div className="space-y-0.5">
                    <h2 className="text-sm font-semibold leading-130">
                      {task.name}
                    </h2>
                    <p className="text-xs text-orange-100 flex-y-center gap-0.5">
                      +{formatNumber(task.award)}{" "}
                      <IconDon className="size-[14px]" />
                    </p>
                  </div>
                </div>
                <span
                  onClick={() => {
                    if (task.link) {
                      openLink(task.link);
                    }
                  }}
                  className="flex-center size-8 cursor-pointer rounded-full bg-orange-100"
                >
                  <ExternalLink className="size-5 text-dark" />
                </span>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center gap-3 mb-6 pt-5">
              <div className="flex flex-col gap-2 w-full">
                <Label
                  htmlFor="promocode"
                  className="text-base leading-130 font-semibold text-white"
                >
                  {t("task_status.PERFORMED")}
                </Label>
                <div className="w-full relative">
                  <Input
                    type="text"
                    ref={inputRef}
                    id="promocode"
                    placeholder={t("enter_promo_code")}
                    className="border-white/08 pr-[calc(70px+1rem)]"
                    value={inputValue}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={handlePaste}
                    className="py-1.5 z-10 px-1 w-[70px] text-xs font-medium leading-120 rounded-sm text-gray-1 bg-white/08 absolute top-1/2 -translate-y-1/2 right-2"
                  >
                    {t("paste")}
                  </button>
                </div>
              </div>
            </div>
            <BaseButton
              type="submit"
              text={t("task_status.PERFORMED")}
              className={cn(
                "w-full border border-transparent disabled:border-white/08"
              )}
              isLoading={isPending}
              disabled={inputValue === ""}
            />
          </form>
        </DrawerContent>
      </Drawer>
    </Fragment>
  );
};

export default PromocodeDrawer;
